import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { isApiError, upload, type MediaDetail } from "@motolynk/api-client";
import type { UploadItem } from "@motolynk/ui";

import { MEDIA_API, mediaKeys, type MediaPanel } from "./api";

const PARALLEL = 3;
const CLEAR_DONE_MS = 4000;

export interface UploadOptions {
  visibility?: "public" | "private";
  folder?: string | null;
}

/**
 * Upload files to the panel's media library with per-file progress (three at a time). Server validation errors
 * ("This file type isn't allowed.") are shown per file; finished files disappear after a few seconds.
 */
export function useMediaUpload(panel: MediaPanel, onUploaded?: (media: MediaDetail) => void) {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<UploadItem[]>([]);

  const patch = (id: string, change: Partial<UploadItem>) =>
    setUploads((all) => all.map((u) => (u.id === id ? { ...u, ...change } : u)));

  const uploadFiles = useCallback(
    async (files: File[], options: UploadOptions = {}) => {
      const items = files.map((file) => ({
        file,
        item: {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: file.name,
          progress: 0,
          status: "uploading" as const,
        },
      }));
      setUploads((all) => [...all, ...items.map((i) => i.item)]);

      const queue = [...items];
      const worker = async () => {
        for (let next = queue.shift(); next; next = queue.shift()) {
          const { file, item } = next;
          const form = new FormData();
          form.append("file", file);
          form.append("visibility", options.visibility ?? "public");
          if (options.folder) form.append("folder", options.folder);
          try {
            const { data } = await upload<MediaDetail>(MEDIA_API[panel].base, form, (p) =>
              patch(item.id, { progress: p }),
            );
            patch(item.id, { status: "done", progress: 1 });
            onUploaded?.(data);
            setTimeout(
              () => setUploads((all) => all.filter((u) => u.id !== item.id)),
              CLEAR_DONE_MS,
            );
          } catch (error) {
            const message = isApiError(error)
              ? (error.fieldError("file") ?? error.message)
              : String(error);
            patch(item.id, { status: "error", error: message });
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(PARALLEL, items.length) }, worker));
      await queryClient.invalidateQueries({ queryKey: mediaKeys.all(panel) });
    },
    [panel, queryClient, onUploaded],
  );

  const dismissErrors = () => setUploads((all) => all.filter((u) => u.status !== "error"));
  return { uploads, uploadFiles, dismissErrors };
}
